// Real sharing backend, ported from the legacy app's SharingDialog/projectService.
// Sharing is project-level only — the schema has no document-level sharing
// concept, so a document's share modal resolves to its parent project before
// reaching this service (see ShareModal.vue).
import { GraphqlAPI } from "../../core/graphql-api";

const graphqlServer = import.meta.env.VITE_GRAPHQL_SERVER_V1;

export const ROLE_LABELS = {
  OWNER: "Owner",
  EDITOR: "Editor",
  CONTRIBUTOR: "Contributor",
  VIEWER: "Viewer",
};

// Roles assignable via invite/role-change — OWNER isn't transferable this way,
// matching the backend's CollaboratorRole enum minus OWNER.
export const ASSIGNABLE_ROLES = ["EDITOR", "CONTRIBUTOR", "VIEWER"];

export default class ShareService {
  constructor() {
    this._graphqlAPIService = null;
  }

  // Built lazily on first use, not in the constructor — see the comment on
  // the same getter in projectService.js: module-level singleton instances of
  // this class can be constructed before login finishes, which would
  // otherwise bake in a missing/stale token permanently.
  get graphqlAPIService() {
    if (!this._graphqlAPIService) {
      this.accessToken = localStorage.getItem("accessToken");
      this._graphqlAPIService = new GraphqlAPI(graphqlServer, this.accessToken);
    }
    return this._graphqlAPIService;
  }

  async getAccess({ projectId }) {
    const data = await this.graphqlAPIService.getProjectCollaborators_v4({ projectId, pageSize: 50 });
    const edges = data?.project?.collaborators?.edges ?? [];
    return {
      projectName: data?.project?.name ?? null,
      publicStatus: data?.project?.publicStatus ?? "PRIVATE",
      collaborators: edges.map(({ node }) => ({
        id: node.id,
        name: node.user?.name || node.user?.email,
        email: node.user?.email,
        role: node.collaboratorRole,
      })),
    };
  }

  // changeCollaboratorRole doubles as invite (role: an ASSIGNABLE_ROLE) and
  // remove (role: null) — there's no separate mutation for either.
  async _changeRole({ projectId, email, role }) {
    const result = await this.graphqlAPIService.shareProject_v4({
      projectId, collaboratorEmail: email, collaboratorRole: role,
    });
    if (result?.message) throw new Error(result.message);
    return result;
  }

  async invite({ projectId, email, role }) {
    await this._changeRole({ projectId, email, role });
    return { id: `${projectId}:${email}`, name: email.split("@")[0], email, role };
  }

  async updateCollaboratorRole({ projectId, email, role }) {
    await this._changeRole({ projectId, email, role });
  }

  async removeCollaborator({ projectId, email }) {
    await this._changeRole({ projectId, email, role: null });
  }
}
